import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';
import { Navigate } from 'react-router-dom';
import { api } from '../utils/api';
import type { AppId } from '@homeapp/shared';

interface AdminUser {
  userId: string;
  name: string;
  role: string;
  mustChangePassword: boolean;
  access: AppId[];
}

interface PendingExercise {
  exerciseId: string;
  type: string;
  level: number;
  language: string;
  topic: string;
  prompt: string;
  answer: string;
  explanation: string;
}

const APPS: AppId[] = ['shopping-list', 'language-learning', 'verb-trainer', 'vocabulary-trainer'];

export default function AdminPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newUser, setNewUser] = useState({ username: '', name: '', password: '' });
  const [message, setMessage] = useState('');
  const [activeTab, setActiveTab] = useState<'users' | 'exercises'>('users');
  const [pendingExercises, setPendingExercises] = useState<PendingExercise[]>([]);
  const [exercisesLoading, setExercisesLoading] = useState(false);
  const [genTopic, setGenTopic] = useState('daily_life');
  const [genLevels, setGenLevels] = useState([1, 2]);
  const [generating, setGenerating] = useState(false);

  const fetchUsers = useCallback(async () => {
    const res = await api.get<{ users: AdminUser[] }>('/admin/users');
    setUsers(res.users);
    setLoading(false);
  }, []);

  const fetchPendingExercises = useCallback(async () => {
    setExercisesLoading(true);
    try {
      const res = await api.get<{ exercises: PendingExercise[] }>('/learning/admin/pending');
      setPendingExercises(res.exercises || []);
    } catch {
      setPendingExercises([]);
    } finally {
      setExercisesLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
    fetchPendingExercises();
  }, [fetchUsers, fetchPendingExercises]);

  if (user?.role !== 'admin') return <Navigate to="/" replace />;

  const handleAddUser = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await api.post('/admin/users', newUser);
      setNewUser({ username: '', name: '', password: '' });
      setShowAddForm(false);
      setMessage(t('admin.userAdded'));
      await fetchUsers();
    } catch (err) {
      setMessage(err instanceof Error ? err.message : 'Error');
    }
  };

  const handleDeleteUser = async (userId: string) => {
    if (!confirm(t('admin.confirmDelete'))) return;
    await api.delete(`/admin/users/${userId}`);
    setMessage(t('admin.userRemoved'));
    await fetchUsers();
  };

  const handleResetPassword = async (userId: string) => {
    const pw = prompt('New password (min 8 chars):');
    if (!pw || pw.length < 8) return;
    await api.put(`/admin/users/${userId}/password`, { newPassword: pw });
    setMessage(t('admin.passwordReset'));
    await fetchUsers();
  };

  const handleToggleAccess = async (userId: string, app: AppId, hasAccess: boolean) => {
    await api.put(`/admin/users/${userId}/access`, { app, grant: !hasAccess });
    setMessage(t('admin.accessUpdated'));
    await fetchUsers();
  };

  const handleGenerate = async () => {
    setGenerating(true);
    let totalCount = 0;
    try {
      // Generate one level at a time to stay within API Gateway 30s timeout
      for (const level of genLevels) {
        setMessage(`Generating level ${level} exercises...`);
        const res = await api.post<{ exerciseCount: number }>('/learning/admin/generate', {
          topic: genTopic,
          language: 'dutch',
          levels: [level],
          count: 3,
        });
        totalCount += res.exerciseCount;
      }
      setMessage(`Generated ${totalCount} exercises for review`);
      await fetchPendingExercises();
    } catch (err) {
      setMessage(`Generated ${totalCount} so far. Error: ${err instanceof Error ? err.message : 'Generation failed'}`);
    } finally {
      setGenerating(false);
    }
  };

  const handleApprove = async (exerciseId: string) => {
    await api.post(`/learning/admin/approve/${exerciseId}`, {});
    setMessage('Exercise approved');
    setPendingExercises(prev => prev.filter(e => e.exerciseId !== exerciseId));
  };

  const handleReject = async (exerciseId: string) => {
    await api.post(`/learning/admin/reject/${exerciseId}`, {});
    setPendingExercises(prev => prev.filter(e => e.exerciseId !== exerciseId));
  };

  if (loading) return <div className="flex items-center justify-center h-64">{t('app.loading')}</div>;

  return (
    <div className="px-4 py-6 max-w-4xl mx-auto">
      <h1 className="text-xl font-bold text-gray-800 mb-4">⚙️ {t('admin.title')}</h1>

      {message && (
        <div className="bg-green-50 text-green-700 px-4 py-2 rounded-lg mb-4 text-sm">
          {message}
          <button onClick={() => setMessage('')} className="float-right text-green-500">✕</button>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 mb-6">
        <button
          onClick={() => setActiveTab('users')}
          className={`px-4 py-2 rounded-xl text-sm font-medium transition ${activeTab === 'users' ? 'bg-gray-800 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
        >
          👥 Users
        </button>
        <button
          onClick={() => setActiveTab('exercises')}
          className={`px-4 py-2 rounded-xl text-sm font-medium transition ${activeTab === 'exercises' ? 'bg-gray-800 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
        >
          📚 Exercises {pendingExercises.length > 0 && <span className="ml-1 bg-red-500 text-white rounded-full px-1.5 text-xs">{pendingExercises.length}</span>}
        </button>
      </div>

      {/* ─── Users Tab ─── */}
      {activeTab === 'users' && (
        <>
          <div className="flex justify-end mb-4">
            <button
              onClick={() => setShowAddForm(!showAddForm)}
              className="bg-gray-700 text-white px-4 py-2 rounded-xl text-sm font-medium hover:bg-gray-800 transition"
            >
              + {t('admin.addUser')}
            </button>
          </div>

          {showAddForm && (
            <form onSubmit={handleAddUser} className="bg-white rounded-xl p-4 mb-4 shadow-sm space-y-3">
              <div className="grid grid-cols-3 gap-3">
                <input
                  placeholder="Username"
                  value={newUser.username}
                  onChange={e => setNewUser(u => ({ ...u, username: e.target.value }))}
                  className="px-3 py-2 rounded-lg border border-gray-300 text-sm"
                  required
                />
                <input
                  placeholder="Display Name"
                  value={newUser.name}
                  onChange={e => setNewUser(u => ({ ...u, name: e.target.value }))}
                  className="px-3 py-2 rounded-lg border border-gray-300 text-sm"
                  required
                />
                <input
                  placeholder="Password"
                  type="password"
                  value={newUser.password}
                  onChange={e => setNewUser(u => ({ ...u, password: e.target.value }))}
                  className="px-3 py-2 rounded-lg border border-gray-300 text-sm"
                  required
                  minLength={8}
                />
              </div>
              <button type="submit" className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium">
                {t('admin.addUser')}
              </button>
            </form>
          )}

          <div className="space-y-3">
            {users.map(u => (
              <div key={u.userId} className="bg-white rounded-xl p-4 shadow-sm">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <span className="font-semibold text-gray-800">{u.name}</span>
                    <span className="text-gray-400 text-sm ml-2">@{u.userId}</span>
                    {u.role === 'admin' && (
                      <span className="ml-2 px-2 py-0.5 bg-amber-100 text-amber-700 rounded text-xs font-medium">Admin</span>
                    )}
                    {u.mustChangePassword && (
                      <span className="ml-2 px-2 py-0.5 bg-red-100 text-red-600 rounded text-xs">Must change password</span>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleResetPassword(u.userId)}
                      className="text-sm text-blue-600 hover:text-blue-800"
                    >
                      {t('admin.resetPassword')}
                    </button>
                    {u.role !== 'admin' && (
                      <button
                        onClick={() => handleDeleteUser(u.userId)}
                        className="text-sm text-red-500 hover:text-red-700"
                      >
                        {t('admin.removeUser')}
                      </button>
                    )}
                  </div>
                </div>

                <div className="flex gap-2">
                  {APPS.map(app => {
                    const hasAccess = u.access.includes(app);
                    return (
                      <button
                        key={app}
                        onClick={() => handleToggleAccess(u.userId, app, hasAccess)}
                        className={`px-3 py-1 rounded-full text-xs font-medium transition ${
                          hasAccess
                            ? 'bg-green-100 text-green-700 hover:bg-green-200'
                            : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
                        }`}
                      >
                        {hasAccess ? '✓' : '○'} {app}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* ─── Exercises Tab ─── */}
      {activeTab === 'exercises' && (
        <>
          {/* Generator */}
          <div className="bg-white rounded-xl p-4 shadow-sm mb-6">
            <h3 className="text-sm font-semibold text-gray-600 mb-3">Generate New Exercises (AI)</h3>
            <div className="flex flex-wrap gap-3 items-end">
              <div>
                <label className="block text-xs text-gray-400 mb-1">Topic</label>
                <select
                  value={genTopic}
                  onChange={e => setGenTopic(e.target.value)}
                  className="px-3 py-2 rounded-lg border border-gray-300 text-sm"
                >
                  <option value="daily_life">Daily Life</option>
                  <option value="shopping">Shopping</option>
                  <option value="work">Work</option>
                  <option value="family">Family</option>
                  <option value="travel">Travel</option>
                  <option value="health">Health</option>
                  <option value="food">Food & Cooking</option>
                  <option value="school">School</option>
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">Levels</label>
                <div className="flex gap-2">
                  {[1, 2, 3, 4].map(lv => (
                    <button
                      key={lv}
                      onClick={() => setGenLevels(prev => prev.includes(lv) ? prev.filter(l => l !== lv) : [...prev, lv])}
                      className={`w-8 h-8 rounded-lg text-xs font-medium ${genLevels.includes(lv) ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-500'}`}
                    >
                      {lv}
                    </button>
                  ))}
                </div>
              </div>
              <button
                onClick={handleGenerate}
                disabled={generating || genLevels.length === 0}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-40"
              >
                {generating ? 'Generating...' : '🤖 Generate'}
              </button>
            </div>
          </div>

          {/* Pending exercises list */}
          <h3 className="text-sm font-semibold text-gray-600 mb-3">
            Pending Review ({pendingExercises.length})
          </h3>

          {exercisesLoading ? (
            <p className="text-gray-400 text-sm">Loading...</p>
          ) : pendingExercises.length === 0 ? (
            <p className="text-gray-400 text-sm">No exercises pending review. Generate some above!</p>
          ) : (
            <div className="space-y-3">
              {pendingExercises.map(ex => (
                <div key={ex.exerciseId} className="bg-white rounded-xl p-4 shadow-sm">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="px-2 py-0.5 bg-indigo-100 text-indigo-600 rounded text-xs font-medium">
                      Lv.{ex.level}
                    </span>
                    <span className="px-2 py-0.5 bg-gray-100 text-gray-500 rounded text-xs">
                      {ex.type}
                    </span>
                    <span className="text-xs text-gray-400">{ex.topic}</span>
                  </div>
                  <p className="text-sm text-gray-700 mb-1"><strong>Prompt:</strong> {ex.prompt}</p>
                  <p className="text-sm text-gray-700 mb-1"><strong>Answer:</strong> {ex.answer}</p>
                  {ex.explanation && (
                    <p className="text-xs text-gray-500 mb-2"><strong>Explanation:</strong> {ex.explanation}</p>
                  )}
                  <div className="flex gap-2 mt-2">
                    <button
                      onClick={() => handleApprove(ex.exerciseId)}
                      className="px-3 py-1.5 bg-green-600 text-white rounded-lg text-xs font-medium hover:bg-green-700"
                    >
                      ✓ Approve
                    </button>
                    <button
                      onClick={() => handleReject(ex.exerciseId)}
                      className="px-3 py-1.5 bg-red-500 text-white rounded-lg text-xs font-medium hover:bg-red-600"
                    >
                      ✕ Reject
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
